import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { clearAuthStorage } from '@/utils/auth'
import { resetAppTabs } from '@/composables/useTabs'
import { hasPermission } from '@/utils/permission'

let lastProfileSyncAt = 0

export const useUserStore = defineStore('user', () => {
  const user = ref(null)
  const token = ref(localStorage.getItem('token') || '')
  
  const isSuperuser = computed(() => {
    return user.value?.is_superuser || false
  })

  const isAdmin = computed(() => {
    return user.value?.is_admin || false
  })

  /** 移动端料号交易完整版（含客户/供应商） */
  const canViewInventorySlotTransactionsFull = computed(
    () =>
      isSuperuser.value ||
      isAdmin.value ||
      hasPermission(user.value, 'inventory.mobile_batch_tx.view_full')
  )

  /** 可看移动端料号交易（完整或脱敏） */
  const canViewInventorySlotTransactions = computed(
    () =>
      canViewInventorySlotTransactionsFull.value ||
      hasPermission(user.value, 'inventory.mobile_batch_tx.view')
  )
  
  const isLoggedIn = computed(() => {
    return !!token.value
  })
  
  function setUser(userData) {
    user.value = userData
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData))
    } else {
      localStorage.removeItem('user')
    }
  }
  
  function setToken(newToken) {
    token.value = newToken
    localStorage.setItem('token', newToken)
  }
  
  function clearUser() {
    user.value = null
    token.value = ''
    lastProfileSyncAt = 0
    clearAuthStorage()
    resetAppTabs()
  }

  /**
   * 从后端拉取当前 token 对应的用户（含角色权限），写入 store 与 localStorage。
   * 解决：管理员改角色权限后，前端仍用旧 localStorage 导致菜单/路由误判无权限。
   */
  async function syncUserFromServer(options = {}) {
    const { force = false } = options
    const minIntervalMs = force ? 0 : 5 * 60_000
    const t = token.value || localStorage.getItem('token')
    if (!t) return
    if (!force && Date.now() - lastProfileSyncAt < minIntervalMs) {
      return
    }
    try {
      const request = (await import('@/utils/request')).default
      // 云端 verify 挂起时避免路由守卫长时间 await（默认 axios 20s 也会拖死首屏）
      const res = await request({
        url: '/api/auth/verify',
        method: 'post',
        timeout: 12000,
        skipAuthRedirect: true
      })
      if (res?.success && res.user) {
        user.value = res.user
        localStorage.setItem('user', JSON.stringify(res.user))
        lastProfileSyncAt = Date.now()
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        clearUser()
        const { forceAuthRelogin } = await import('@/utils/auth')
        await forceAuthRelogin(
          e?.response?.data?.message || '登录已失效，请重新登录'
        )
      }
      // 其它错误保留 initFromStorage 缓存，由业务接口提示
    }
  }
  
  // 从localStorage初始化用户信息
  function initFromStorage() {
    token.value = localStorage.getItem('token') || ''
    const userData = localStorage.getItem('user')
    if (userData) {
      try {
        user.value = JSON.parse(userData)
      } catch (error) {
        console.error('Failed to parse user data from localStorage:', error)
      }
    } else {
      user.value = null
    }
  }
  
  return {
    user,
    token,
    isSuperuser,
    isAdmin,
    canViewInventorySlotTransactions,
    canViewInventorySlotTransactionsFull,
    isLoggedIn,
    setUser,
    setToken,
    clearUser,
    initFromStorage,
    syncUserFromServer
  }
})
